/**
 * Agent Service (Frontend)
 * 
 * Client for the backend agent API. Handles:
 * - Chat communication with the agent
 * - SSE streaming for real-time responses
 * - Conversation management
 * 
 * @module agentService
 */

import { getAuth } from 'firebase/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Get the current user's Firebase ID token for authenticated requests.
 * @returns {Promise<string>}
 */
const getAuthToken = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    return user.getIdToken();
};

/**
 * Returns headers including the Bearer token.
 * @returns {Promise<object>}
 */
const getHeaders = async () => {
    try {
        const token = await getAuthToken();
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    } catch {
        return { 'Content-Type': 'application/json' };
    }
};

/**
 * Parse SSE event data
 */
const parseSSEEvent = (line) => {
    if (line.startsWith('data: ')) {
        try {
            return JSON.parse(line.slice(6));
        } catch (e) {
            console.warn('Failed to parse SSE data:', e);
            return null;
        }
    }
    return null;
};

/**
 * Agent Service class
 */
class AgentService {
    constructor() {
        this.abortController = null;
    }

    /**
     * Cancel any ongoing stream
     */
    cancelStream() {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }

    /**
     * Send a chat message to the agent
     * 
     * @param {object} params - Chat parameters
     * @param {string} params.episodeId - Episode ID
     * @param {string} params.message - User's message
     * @param {string} params.platform - Platform context
     * @param {string} params.contentType - Content type
     * @param {string} [params.conversationId] - Existing conversation ID
     * @param {object} [params.context] - Additional context
     * @param {function} [params.onToolStart] - Callback when tool starts
     * @param {function} [params.onToolComplete] - Callback when tool completes
     * @param {function} [params.onToken] - Callback for streaming tokens
     * @param {function} [params.onContentToken] - Callback for content generation tokens
     * @param {function} [params.onComplete] - Callback when complete
     * @param {function} [params.onError] - Callback on error
     * @returns {Promise<object>} Chat result
     */
    async chat({
        episodeId,
        message,
        platform = 'linkedin',
        contentType = 'post',
        conversationId = null,
        context = {},
        onToolStart,
        onToolComplete,
        onToken,
        onContentToken,
        onComplete,
        onError
    }) {
        // Cancel any existing stream
        this.cancelStream();
        this.abortController = new AbortController();

        try {
            const headers = await getHeaders();
            const response = await fetch(`${API_BASE_URL}/api/agent/chat`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    episodeId,
                    message,
                    platform,
                    contentType,
                    conversationId,
                    context,
                    stream: true
                }),
                signal: this.abortController.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let result = null;

            while (true) {
                const { done, value } = await reader.read();

                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                let currentEvent = '';

                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        currentEvent = line.slice(7).trim();
                    } else if (line.startsWith('data: ')) {
                        const data = parseSSEEvent(line);

                        if (!data) continue;

                        switch (currentEvent) {
                            case 'tool_start':
                                if (onToolStart) {
                                    onToolStart(data);
                                }
                                break;

                            case 'tool_complete':
                                if (onToolComplete) {
                                    onToolComplete(data);
                                }
                                break;

                            case 'token':
                                if (onToken) {
                                    onToken(data.token);
                                }
                                break;

                            case 'content_token':
                                if (onContentToken) {
                                    onContentToken(data.token);
                                }
                                break;

                            case 'complete':
                                result = data;
                                if (onComplete) {
                                    onComplete(data);
                                }
                                break;

                            case 'error':
                                if (onError) {
                                    onError(new Error(data.error || 'Unknown error'));
                                }
                                break;
                        }
                    }
                }
            }

            return result;

        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Agent chat aborted by user');
                return { success: false, aborted: true };
            }

            console.error('Agent chat error:', error);
            if (onError) {
                onError(error);
            }
            throw error;

        } finally {
            this.abortController = null;
        }
    }

    /**
     * Start a new conversation for an episode
     * 
     * @param {object} params - Parameters
     * @param {string} params.episodeId - Episode ID
     * @param {string} params.platform - Platform context
     * @param {string} params.contentType - Content type
     * @param {object} [params.context] - Initial context
     * @returns {Promise<object>} New conversation
     */
    async startNewConversation({ episodeId, platform = 'linkedin', contentType = 'post', context = {} }) {
        try {
            const headers = await getHeaders();
            const response = await fetch(`${API_BASE_URL}/api/agent/conversations/new`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    episodeId,
                    platform,
                    contentType,
                    context
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();

        } catch (error) {
            console.error('Start new conversation error:', error);
            throw error;
        }
    }

    /**
     * Get the current conversation for an episode
     * 
     * @param {string} episodeId - Episode ID
     * @returns {Promise<object>} Current conversation
     */
    async getCurrentConversation(episodeId) {
        try {
            const headers = await getHeaders();
            const response = await fetch(`${API_BASE_URL}/api/agent/conversations/${episodeId}`, {
                method: 'GET',
                headers
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();

        } catch (error) {
            console.error('Get conversation error:', error);
            throw error;
        }
    }

    /**
     * Clear the current conversation for an episode
     * 
     * @param {string} episodeId - Episode ID
     * @returns {Promise<object>} Result
     */
    async clearConversation(episodeId) {
        try {
            const headers = await getHeaders();
            const response = await fetch(`${API_BASE_URL}/api/agent/conversations/${episodeId}`, {
                method: 'DELETE',
                headers
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();

        } catch (error) {
            console.error('Clear conversation error:', error);
            throw error;
        }
    }

    /**
     * Get available tools for a content type
     * 
     * @param {string} contentType - Content type
     * @returns {Promise<object>} Available tools
     */
    async getAvailableTools(contentType = 'post') {
        try {
            const headers = await getHeaders();
            const response = await fetch(`${API_BASE_URL}/api/agent/tools?contentType=${contentType}`, {
                method: 'GET',
                headers
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();

        } catch (error) {
            console.error('Get tools error:', error);
            throw error;
        }
    }

    /**
     * Non-streaming chat for simple requests
     * 
     * @param {object} params - Chat parameters
     * @returns {Promise<object>} Chat result
     */
    async chatSimple({ episodeId, message, platform, contentType, conversationId, context }) {
        try {
            const headers = await getHeaders();
            const response = await fetch(`${API_BASE_URL}/api/agent/chat`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    episodeId,
                    message,
                    platform,
                    contentType,
                    conversationId,
                    context,
                    stream: false
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();

        } catch (error) {
            console.error('Simple chat error:', error);
            throw error;
        }
    }
}

// Export singleton instance
const agentService = new AgentService();
export default agentService;